import React, { useState, useEffect } from 'react';
import { Facebook, Instagram, Linkedin, Youtube, Video, Trash2, Bot, X, RefreshCw, CheckCircle2, AlertCircle, Key, Users, Building2 } from 'lucide-react';
import { supabase, withRetry } from '../lib/supabase';
import toast from 'react-hot-toast';
import { FacebookConnect } from './FacebookConnect';
import { InstagramConnect } from './InstagramConnect';
import { InstagramDirectConnect } from './InstagramDirectConnect';
import { LinkedInConnect } from './LinkedInConnect';
import { GoogleBusinessConnect } from './GoogleBusinessConnect';
import { YouTubeConnect } from './YouTubeConnect';
import { TikTokConnect } from './TikTokConnect';
import { XConnect } from './XConnect';
import { ApiSettings } from './ApiSettings';
import { UserApiTokens } from './UserApiTokens';
import { TimezoneSettings } from './TimezoneSettings';
import { validateFacebookAccessToken, refreshPageAccessToken, refreshInstagramAccessToken } from '../lib/facebookService';

interface ConnectedAccount {
  id: string;
  name: string;
  access_token: string;
  account_type: string;
  platform: string;
  auto_publish_enabled?: boolean;
  token_expires_at?: string;
  refresh_token?: string;
  status?: string;
}

interface DeleteConfirmationProps {
  account: ConnectedAccount;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmation: React.FC<DeleteConfirmationProps> = ({ account, onConfirm, onCancel }) => (
  <div className="absolute right-0 top-0 mt-8 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
    <div className="flex justify-between items-start mb-3">
      <h3 className="text-sm font-medium text-gray-900">Delete Account</h3>
      <button
        onClick={onCancel}
        className="text-gray-400 hover:text-gray-500"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
    <p className="text-sm text-gray-500 mb-4">
      Are you sure you want to disconnect "{account.name}"? Your scheduled tasks will be preserved and automatically re-linked when you reconnect this account.
    </p>
    <div className="flex justify-end space-x-2">
      <button
        onClick={onCancel}
        className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
      >
        Cancel
      </button>
      <button
        onClick={onConfirm}
        className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
      >
        Delete
      </button>
    </div>
  </div>
);

interface AccountStatusProps {
  isValid: boolean;
  isChecking: boolean;
  platform: string;
  tokenExpiresAt?: string;
}

const EXPIRY_WARNING_DAYS = 7;

const AccountStatus: React.FC<AccountStatusProps> = ({ isValid, isChecking, platform, tokenExpiresAt }) => {
  if (isChecking) {
    return (
      <div className="flex items-center gap-1 text-blue-600">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span className="text-xs">Checking...</span>
      </div>
    );
  }

  // Tokeny stron Facebooka / Instagram Business nie maja daty waznosci,
  // zapisana data jest orientacyjna - nie ostrzegamy na jej podstawie.
  const hasRealExpiry = platform !== 'facebook' && platform !== 'instagram';

  if (isValid) {
    // Check if expiring soon
    if (tokenExpiresAt && hasRealExpiry) {
      const expiresAt = new Date(tokenExpiresAt);
      const now = new Date();
      const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));

      if (daysLeft <= EXPIRY_WARNING_DAYS && daysLeft > 0) {
        return (
          <div className="flex items-center gap-1 text-amber-600">
            <AlertCircle className="w-4 h-4" />
            <span className="text-xs">Wygasa za {daysLeft} {daysLeft === 1 ? 'dzień' : 'dni'}</span>
          </div>
        );
      }
    }

    return (
      <div className="flex items-center gap-1 text-green-600">
        <CheckCircle2 className="w-4 h-4" />
        <span className="text-xs">Active</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-red-600">
      <AlertCircle className="w-4 h-4" />
      <span className="text-xs">Token expired</span>
    </div>
  );
};

const PlatformIcon = ({ platform }: { platform: string }) => {
  switch (platform.toLowerCase()) {
    case 'facebook':
      return <Facebook className="w-5 h-5 text-blue-600" />;
    case 'instagram':
      return <Instagram className="w-5 h-5 text-pink-600" />;
    case 'instagram_direct':
      return <Instagram className="w-5 h-5 text-purple-600" />;
    case 'linkedin':
      return <Linkedin className="w-5 h-5 text-blue-700" />;
    case 'google_business':
      return <Building2 className="w-5 h-5 text-red-600" />;
    case 'youtube':
      return <Youtube className="w-5 h-5 text-red-600" />;
    case 'tiktok':
      return <Video className="w-5 h-5 text-black" />;
    default:
      return null;
  }
};

export const ConnectedAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<ConnectedAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [accountToDelete, setAccountToDelete] = useState<ConnectedAccount | null>(null);
  const [refreshingAccounts, setRefreshingAccounts] = useState<string[]>([]);
  const [accountStatuses, setAccountStatuses] = useState<Record<string, boolean>>({});
  const [checkingStatuses, setCheckingStatuses] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'accounts' | 'api-settings'>('accounts');

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    accounts.forEach(account => {
      checkAccountStatus(account);
    });
  }, [accounts]);

  const loadAccounts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('Not authenticated');
        return;
      }

      const { data, error } = await withRetry(() => {
        return supabase
          .from('connected_accounts')
          .select('*')
          .eq('user_id', user.id);
      });

      if (error) throw error;
      setAccounts(data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error('Failed to load connected accounts');
    } finally {
      setIsLoading(false);
    }
  };

  const checkAccountStatus = async (account: ConnectedAccount) => {
    if (checkingStatuses.includes(account.id)) return;

    setCheckingStatuses(prev => [...prev, account.id]);
    try {
      let isValid = false;
      switch (account.platform) {
        case 'facebook': {
          // First try live Graph API validation
          isValid = await validateFacebookAccessToken(account.access_token);
          // If Graph API fails, fall back to the status verified server-side
          // (validate_meta_tokens sprawdza token bezposrednio w Graph API).
          // Wywolanie z przegladarki potrafi paść przez CORS lub blokade reklam,
          // a tokeny stron nie maja daty waznosci, wiec data nie jest tu dowodem.
          if (!isValid) {
            isValid = account.status === 'active';
          }
          break;
        }
        case 'instagram':
        case 'instagram_direct': {
          // Use the correct API based on token type
          const isDirectToken = account.platform === 'instagram_direct' || 
            (account.access_token && account.access_token.startsWith('IGAA'));
          const validationUrl = isDirectToken
            ? `https://graph.instagram.com/me?fields=id,username&access_token=${account.access_token}`
            : `https://graph.facebook.com/v21.0/me?access_token=${account.access_token}`;
          
          try {
            const response = await fetch(validationUrl,
              { cache: 'no-cache', headers: { 'Accept': 'application/json' } }
            );
            isValid = response.ok;
          } catch {
            isValid = false;
          }
          // Fall back to the server-verified status, then to token_expires_at
          if (!isValid) {
            isValid = account.status === 'active';
          }
          if (!isValid && account.token_expires_at) {
            isValid = new Date(account.token_expires_at) > new Date();
          }
          // Final fallback: if no expiry info available, assume valid
          if (!isValid && !account.token_expires_at && account.access_token) {
            isValid = true;
          }
          break;
        }
        case 'linkedin':
          if (account.token_expires_at) {
            isValid = new Date(account.token_expires_at) > new Date();
          } else {
            isValid = true;
          }
          break;
        case 'google_business':
          if (account.token_expires_at) {
            isValid = new Date(account.token_expires_at) > new Date();
          } else {
            isValid = false;
          }
          break;
        default:
          isValid = false;
      }
      setAccountStatuses(prev => ({ ...prev, [account.id]: isValid }));
    } catch (error) {
      console.error('Error checking account status:', error);
      // On error, fall back to token_expires_at before marking as expired
      const fallbackValid = account.token_expires_at 
        ? new Date(account.token_expires_at) > new Date() 
        : false;
      setAccountStatuses(prev => ({ ...prev, [account.id]: fallbackValid }));
    } finally {
      setCheckingStatuses(prev => prev.filter(id => id !== account.id));
    }
  };

  const handleRemoveAccount = async (account: ConnectedAccount) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      // Delete the connected account.
      // FK ON DELETE SET NULL preserves scheduled_tasks (sets account_id to NULL).
      // When user reconnects the same page, a DB trigger re-links orphaned tasks.
      const { error } = await supabase
        .from('connected_accounts')
        .delete()
        .eq('id', account.id)
        .eq('user_id', user.id);

      if (error) throw error;
      
      setAccounts(accounts.filter(a => a.id !== account.id));
      setAccountToDelete(null);
      toast.success('Konto odłączone. Zaplanowane posty zostaną zachowane i ponownie przypisane po reconnect.');
    } catch (error) {
      console.error('Error removing account:', error);
      toast.error('Failed to remove account');
    }
  };

  const handleRefreshAccount = async (account: ConnectedAccount) => {
    try {
      setRefreshingAccounts(prev => [...prev, account.id]);

      let newToken: string | null = null;

      switch (account.platform) {
        case 'facebook':
          newToken = await refreshPageAccessToken(account.id);
          if (!newToken) {
            throw new Error('Failed to refresh Facebook token. Please reconnect your account.');
          }
          toast.success('Facebook account refreshed successfully');
          break;

        case 'instagram': {
          // Instagram Business tokens use Facebook Graph API
          const igValidationResponse = await fetch(
            `https://graph.facebook.com/v21.0/me?access_token=${account.access_token}`,
            { cache: 'no-cache', headers: { 'Accept': 'application/json' } }
          );
          
          if (igValidationResponse.ok) {
            newToken = await refreshInstagramAccessToken(account.id);
            if (newToken) {
              toast.success('Token Instagram odświeżony pomyślnie');
            } else {
              toast.success('Token Instagram jest aktywny');
              setAccountStatuses(prev => ({ ...prev, [account.id]: true }));
            }
          } else {
            newToken = await refreshInstagramAccessToken(account.id);
            if (!newToken) {
              throw new Error('Token Instagram wygasł. Proszę ponownie podłączyć konto.');
            }
            toast.success('Token Instagram odświeżony pomyślnie');
          }
          break;
        }

        case 'instagram_direct': {
          // Instagram Direct tokens (IGAA...) use Instagram Graph API, NOT Facebook Graph API
          const igDirectValidation = await fetch(
            `https://graph.instagram.com/me?fields=id,username&access_token=${account.access_token}`,
            { cache: 'no-cache', headers: { 'Accept': 'application/json' } }
          );
          
          if (igDirectValidation.ok) {
            // Token is valid — try to refresh it using IG refresh endpoint
            try {
              const refreshResp = await fetch(
                `https://graph.instagram.com/refresh_access_token?grant_type=ig_refresh_token&access_token=${account.access_token}`,
                { cache: 'no-cache', headers: { 'Accept': 'application/json' } }
              );
              if (refreshResp.ok) {
                const refreshData = await refreshResp.json();
                if (refreshData.access_token) {
                  const tokenExpiresAt = new Date(Date.now() + (refreshData.expires_in || 5184000) * 1000).toISOString();
                  await supabase
                    .from('connected_accounts')
                    .update({ access_token: refreshData.access_token, token_expires_at: tokenExpiresAt })
                    .eq('id', account.id);
                  newToken = refreshData.access_token;
                  toast.success('Token Instagram Direct odświeżony pomyślnie');
                } else {
                  toast.success('Token Instagram Direct jest aktywny');
                }
              } else {
                // Refresh failed but token is still valid
                toast.success('Token Instagram Direct jest aktywny');
              }
            } catch {
              toast.success('Token Instagram Direct jest aktywny');
            }
            setAccountStatuses(prev => ({ ...prev, [account.id]: true }));
          } else {
            throw new Error('Token Instagram Direct wygasł. Proszę ponownie podłączyć konto.');
          }
          break;
        }

        case 'linkedin': {
          // Check if token is actually expired before showing error
          const isTokenValid = account.token_expires_at && new Date(account.token_expires_at) > new Date();
          if (isTokenValid) {
            toast.success('Token LinkedIn jest aktywny i ważny do ' + new Date(account.token_expires_at!).toLocaleDateString('pl-PL') + '.');
            setAccountStatuses(prev => ({ ...prev, [account.id]: true }));
          } else {
            toast.error('Token LinkedIn wygasł. Proszę ponownie podłączyć konto.');
            setAccountStatuses(prev => ({ ...prev, [account.id]: false }));
          }
          break;
        }

        case 'google_business': {
          // Refresh Google Business token using the edge function
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const refreshResponse = await fetch(`${supabaseUrl}/functions/v1/google-business-token-refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({ accountId: account.id }),
          });

          if (!refreshResponse.ok) {
            const errData = await refreshResponse.json().catch(() => ({}));
            throw new Error(errData.error || 'Failed to refresh Google Business token');
          }

          const refreshData = await refreshResponse.json();
          if (refreshData.results?.[0]?.success) {
            newToken = refreshData.results[0].new_access_token || account.access_token;
            toast.success('Google Business token refreshed successfully!');
          } else {
            throw new Error(refreshData.results?.[0]?.error || 'Token refresh failed. Please reconnect your account.');
          }
          break;
        }

        default:
          throw new Error('Unknown platform');
      }

      if (newToken) {
        setAccounts(prev =>
          prev.map(a =>
            a.id === account.id
              ? { ...a, access_token: newToken }
              : a
          )
        );
        setAccountStatuses(prev => ({ ...prev, [account.id]: true }));
      }
    } catch (error) {
      console.error('Error refreshing account:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to refresh account. Please reconnect.';
      
      // Before marking as expired, re-validate the original token
      // The refresh mechanism may fail (wrong app credentials, ad blocker) but the token itself could still be valid
      if (account.platform === 'facebook') {
        const stillValid = await validateFacebookAccessToken(account.access_token);
        // Also check token_expires_at as fallback (ad blocker may block Graph API)
        const tokenStillFresh = account.token_expires_at && new Date(account.token_expires_at) > new Date();
        if (stillValid || tokenStillFresh) {
          toast('Token jest nadal aktywny. Odświeżanie nie powiodło się, ale token działa.', { icon: 'ℹ️' });
          setAccountStatuses(prev => ({ ...prev, [account.id]: true }));
          return;
        }
      } else if (account.platform === 'instagram' || account.platform === 'instagram_direct') {
        // For Instagram, check if token_expires_at is in the future
        if (account.token_expires_at && new Date(account.token_expires_at) > new Date()) {
          toast('Token Instagram jest nadal aktywny.', { icon: 'ℹ️' });
          setAccountStatuses(prev => ({ ...prev, [account.id]: true }));
          return;
        }
      }
      
      toast.error(errorMessage);
      setAccountStatuses(prev => ({ ...prev, [account.id]: false }));
    } finally {
      setRefreshingAccounts(prev => prev.filter(id => id !== account.id));
    }
  };

  const handleToggleAutoPublish = async (account: ConnectedAccount) => {
    try {
      const newValue = !(account.auto_publish_enabled !== false);
      
      const { error } = await supabase
        .from('connected_accounts')
        .update({ auto_publish_enabled: newValue })
        .eq('id', account.id);

      if (error) throw error;

      setAccounts(prev =>
        prev.map(a =>
          a.id === account.id
            ? { ...a, auto_publish_enabled: newValue }
            : a
        )
      );

      toast.success(
        newValue
          ? 'Posty będą publikowane automatycznie'
          : 'Posty będą wymagały ręcznej akceptacji'
      );
    } catch (error) {
      console.error('Error toggling auto publish:', error);
      toast.error('Nie udało się zmienić ustawienia');
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center space-x-3 mb-6">
          <Bot className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-xl font-bold">AI Agent HUB</h1>
            <p className="text-sm text-gray-500">Social Media Scheduler</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 flex-1">
          <h2 className="text-xl font-semibold mb-6">Connected Accounts</h2>
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center space-x-3 mb-6">
        <Bot className="w-8 h-8 text-blue-600" />
        <div className="flex-1">
          <div>
            <h1 className="text-xl font-bold">AI Agent HUB</h1>
            <p className="text-sm text-gray-500">Social Media Scheduler</p>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow p-3 sm:p-4 lg:p-6 flex-1 mb-4">
        <div className="flex items-center space-x-2 sm:space-x-4 mb-4 sm:mb-6 border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('accounts')}
            className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 sm:py-3 font-medium transition-colors border-b-2 whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'accounts'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Users className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Accounts</span>
          </button>
          <button
            onClick={() => setActiveTab('api-settings')}
            className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 sm:py-3 font-medium transition-colors border-b-2 whitespace-nowrap text-sm sm:text-base ${
              activeTab === 'api-settings'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Key className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>API Settings</span>
          </button>
        </div>

        {activeTab === 'accounts' ? (
          <div className="space-y-4">
          {accounts
            .filter(account => !['youtube', 'tiktok', 'tiktok_business'].includes(account.platform))
            .map((account) => (
            <div
              key={account.id}
              className="relative flex flex-col p-3 sm:p-4 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 min-w-0">
                  <PlatformIcon platform={account.platform} />
                  <div className="min-w-0">
                    <p className="font-medium truncate">{account.name}</p>
                    <AccountStatus 
                      isValid={accountStatuses[account.id] || false}
                      isChecking={checkingStatuses.includes(account.id)}
                      platform={account.platform}
                      tokenExpiresAt={account.token_expires_at}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRefreshAccount(account)}
                    disabled={refreshingAccounts.includes(account.id)}
                    className={`p-1.5 rounded-full flex-shrink-0 ${
                      refreshingAccounts.includes(account.id)
                        ? 'text-blue-400 bg-blue-50'
                        : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    <RefreshCw className={`w-4 h-4 ${
                      refreshingAccounts.includes(account.id) ? 'animate-spin' : ''
                    }`} />
                  </button>
                  <button
                    onClick={() => setAccountToDelete(account)}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50 flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {accountToDelete?.id === account.id && (
                <DeleteConfirmation
                  account={account}
                  onConfirm={() => handleRemoveAccount(account)}
                  onCancel={() => setAccountToDelete(null)}
                />
              )}
              {account.platform === 'linkedin' && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Auto-publikacja</span>
                    </div>
                    <button
                      onClick={() => handleToggleAutoPublish(account)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        account.auto_publish_enabled !== false
                          ? 'bg-blue-600'
                          : 'bg-gray-300'
                      }`}
                      title={account.auto_publish_enabled !== false
                        ? 'Posty publikowane automatycznie'
                        : 'Posty wymagają ręcznej akceptacji'}
                    >
                      <span
                        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          account.auto_publish_enabled !== false
                            ? 'translate-x-4'
                            : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          </div>
        ) : (
          <div className="space-y-6">
            <UserApiTokens />
            <TimezoneSettings />
            <ApiSettings />
          </div>
        )}
      </div>

      <div className="sticky bottom-0 left-0 w-full bg-white p-4 border-t">
        <img 
          src="https://gregciupek.com/wp-content/uploads/2025/02/grafika-n8n-700-x-200-px.png"
          alt="AI Automation"
          className="w-full h-auto rounded-lg shadow-lg"
        />
      </div>
    </div>
  );
};